import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Utils } from "../../../utils";
import moment from "../../../utils/moment";
import { TrustapUserService } from "../../../services";
import {
  useMyListings,
  useSellerCounts,
  useSellerBidsYearly,
  useSellerSalesYearly,
  useMyTrustapAccount,
} from "../../../hooks/queries";
import { usePersistStore, useStore } from "../../../store";
import Table from "../../../components/Table";
import AdStatusDropdown from "../../../components/AdStatusDropdown";
import AdSortingDropdown from "../../../components/AdSortingDropdown";
import TableSearchInput from "../../../components/TableSearchInput";
import { Button } from "../../../controls";
import { AD_STATUS } from "../../../utils/enums";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton } from "boneyard-js/react";
import { AdStatusChip } from "../../../components/status";
import StatCard from "../../../components/StatCard";
import { useResolvedTheme } from "../../../hooks/useTheme";

// Module-scoped so it has a stable identity across Seller re-renders.
// Defined inside `Seller`, this would be redeclared on every render
// (e.g. when a chart-toggle button flips reportType state) — React
// would treat the new function as a new component type, unmount the
// old ArtworkList, and remount a fresh one. That remount resets all
// internal state and triggers the reload effect, refetching the table
// every time the user clicks a chart button.
const ArtworkList: React.FC = () => {
  const [status, setStatus] = useState("");
  const [sorting, setSorting] = useState("");
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);

  const { data, isLoading, error } = useMyListings(token, {
    keyword: appliedKeyword,
    page,
    limit: rowsPerPage,
    status,
    sorting: sorting || "newest-first",
  });
  const list = data?.ads || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const renderPercentageChange = useCallback((row: any) => {
    if (row?.status !== AD_STATUS.SOLD) return "-";
    return Utils.formatPercentChange(row?.price, row?.priceSold, 1);
  }, []);

  const renderBadge = useCallback(
    (row: any) => (
      <AdStatusChip status={row?.status} biddingEnds={row?.biddingEnds} />
    ),
    []
  );

  const columns = useMemo(
    () => [
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/details/${row?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.price),
      },
      {
        title: "Selling Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) =>
          row?.status !== AD_STATUS.SOLD
            ? "-"
            : Utils.formatToCurrency(row?.priceSold),
      },
      {
        title: "% Change",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderPercentageChange(row),
      },
      {
        title: "Bidding Starts",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingStarts),
      },
      {
        title: "Closes at",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingEnds),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row),
      },
    ],
    [renderPercentageChange, renderBadge]
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <Link to={`/details/${row?._id}`} className="bidsCard__title">
            {row?.title}
          </Link>
          <div className="bidsCard__status">{renderBadge(row)}</div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.price)}
            </span>
          </div>
          {row?.status === AD_STATUS.SOLD ? (
            <>
              <div className="bidsCard__stat">
                <span className="bidsCard__statLabel">Sold</span>
                <span className="bidsCard__statValue">
                  {Utils.formatToCurrency(row?.priceSold)}
                </span>
              </div>
              <div className="bidsCard__stat">
                <span className="bidsCard__statLabel">Change</span>
                <span className="bidsCard__statValue">
                  {renderPercentageChange(row)}
                </span>
              </div>
            </>
          ) : (
            <div className="bidsCard__stat bidsCard__stat--span2">
              <span className="bidsCard__statLabel">Status</span>
              <span className="bidsCard__statValue">
                {renderBadge(row)}
              </span>
            </div>
          )}
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>Starts {Utils.formatDateTime(row?.biddingStarts)}</span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-hourglass-split" aria-hidden="true"></i>
            <span>Closes {Utils.formatDateTime(row?.biddingEnds)}</span>
          </div>
        </div>
      </div>
    ),
    [renderBadge, renderPercentageChange]
  );

  return (
    <div className="chartCard py-4">
      <div className="tableCardHeader px-4">
        <div className="tableCardHeader__titleBlock">
          <h2 className="tableCardHeader__title m-0">My Listing</h2>
          <p className="tableCardHeader__subtitle m-0">
            {isDone
              ? `${count} ${count === 1 ? "listing" : "listings"} total`
              : "Loading your listings…"}
          </p>
        </div>
        <div className="tableCardHeader__actions customTitle">
          <AdStatusDropdown value={status} onChange={setStatus} />
          <AdSortingDropdown value={sorting} onChange={setSorting} />
          <TableSearchInput
            value={keyword}
            onChange={setKeyword}
            onEnter={() => setAppliedKeyword(keyword)}
          />
        </div>
      </div>
      <Table
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        page={page}
        setPage={setPage}
        columns={columns}
        rows={list}
        isDone={isDone}
        count={count}
        mobileCard={renderMobileCard}
      />
    </div>
  );
};

function Seller() {
  const navigate = useNavigate();
  const { setErrorMessage } = useStore((s: any) => s);
  const { token, isVerifiedUser, user } = usePersistStore((s: any) => s);

  const handleCreateClick = async () => {
    let hasFullTrustapAccount = false;
    if (token) {
      const result = await TrustapUserService.getMyTrustapAccount(token);
      hasFullTrustapAccount = result?.data === true;
    }
    const gate = Utils.canCreateListing(
      user,
      isVerifiedUser,
      hasFullTrustapAccount
    );
    if (gate.ok) {
      navigate("/dashboard/create");
    } else if (gate.reason === "no-full-trustap") {
      navigate("/dashboard/trustap-account");
    } else {
      navigate("/dashboard/profile");
    }
  };
  const [reportType, setReportType] = useState("total-bids");

  const { data: counts } = useSellerCounts(token);
  // Only flag "no active listings" once counts have actually loaded, so the
  // banner doesn't flash during the initial fetch.
  const noArtworks = !!counts && Number(counts?.active || 0) === 0;

  const { data: trustapAccount, error: trustapError } =
    useMyTrustapAccount(token);
  // Default to "created" until the check resolves, to avoid flashing the
  // "create your Trustap account" banner on load.
  const isTrustapAccountCreated = trustapAccount ?? true;

  useEffect(() => {
    if (trustapError) setErrorMessage((trustapError as Error).message);
  }, [trustapError, setErrorMessage]);


  const SellerYearlyBids = () => {
    const resolvedTheme = useResolvedTheme();
    const isDark = resolvedTheme === "dark";
    const gridBorder = isDark ? "#1f1f1f" : "#e0e0e0";
    const axisText = isDark ? "#888888" : "#6e6e6e";
    const options: ApexOptions = {
      plotOptions: {
        bar: {
          columnWidth: 28,
          borderRadius: 6,
          borderRadiusApplication: "end",
        },
      },
      chart: {
        type: "bar",
        height: "100%",
        zoom: { enabled: false },
        toolbar: { show: false },
        foreColor: axisText,
        background: "transparent",
      },
      colors: ["#51806F", "#CBD9D4"],
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        lineCap: "round",
      },
      xaxis: {
        categories: Array.apply(0, Array(12)).map(function (_, i) {
          return moment().month(i).format("MMM YYYY");
        }),
        labels: { show: true },
        axisBorder: { show: true },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          show: true,
          formatter: (val) => Utils.formatToNumber(val.toString()),
        },
        axisBorder: { show: true },
      },
      grid: {
        show: true,
        borderColor: gridBorder,
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
      },
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: undefined,
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
    };
    const { data: rows } = useSellerBidsYearly(token, moment().year());
    const data = useMemo(() => {
      if (!(rows && rows.length > 0)) return [];
      const _data: any[] = [];
      const _l = rows.map((k: any) => parseInt(moment(k._id).format("M")));
      for (let i = 1; i <= 12; i++) {
        if (_l.indexOf(i) === -1) _data.push({ _id: i, total: 0 });
        else {
          const _t = rows.find(
            (k: any) => parseInt(moment(k._id).format("M")) === i
          );
          _data.push({ _id: i, total: _t.total });
        }
      }
      return [{ name: "Total Bids", data: _data.map((k: any) => k.total) }];
    }, [rows]);

    return <Chart options={options} series={data} type="bar" height={330} />;
  };

  const SellerYearlySales = () => {
    const resolvedTheme = useResolvedTheme();
    const isDark = resolvedTheme === "dark";
    const gridBorder = isDark ? "#1f1f1f" : "#e0e0e0";
    const axisText = isDark ? "#888888" : "#6e6e6e";
    const options: ApexOptions = {
      plotOptions: {
        bar: {
          columnWidth: 28,
          borderRadius: 6,
          borderRadiusApplication: "end",
        },
      },
      chart: {
        type: "bar",
        height: "100%",
        zoom: { enabled: false },
        toolbar: { show: false },
        foreColor: axisText,
        background: "transparent",
      },
      colors: ["#51806F", "#CBD9D4"],
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        lineCap: "round",
      },
      xaxis: {
        categories: Array.apply(0, Array(12)).map(function (_, i) {
          return moment().month(i).format("MMM YYYY");
        }),
        labels: { show: true },
        axisBorder: { show: true },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          show: true,
          formatter: (val) => Utils.formatToCurrency(val.toString()),
        },
        axisBorder: { show: true },
      },
      grid: {
        show: true,
        borderColor: gridBorder,
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
      },
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: undefined,
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
    };
    const { data: rows } = useSellerSalesYearly(token, moment().year());
    const data = useMemo(() => {
      if (!(rows && rows.length > 0)) return [];
      const _data: any[] = [];
      const _l = rows.map((k: any) => parseInt(moment(k._id).format("M")));
      for (let i = 1; i <= 12; i++) {
        if (_l.indexOf(i) === -1) _data.push({ _id: i, total: 0 });
        else {
          const _t = rows.find(
            (k: any) => parseInt(moment(k._id).format("M")) === i
          );
          _data.push({ _id: i, total: _t?.total });
        }
      }
      return [{ name: "Total Sales", data: _data.map((k: any) => k.total) }];
    }, [rows]);

    return <Chart options={options} series={data} type="bar" height={330} />;
  };

  return (
    <div className="bg-pink p-4">
      {noArtworks && (
        <div
          className="alert alert-warning d-flex justify-content-between align-items-center p-3"
          role="alert"
        >
          <div>
            You have no active listings. Please create a listing to start
            selling your artworks.
          </div>
          <Button
            title="Create Listing"
            onClick={handleCreateClick}
            className="btn-warning"
          />
        </div>
      )}
      {!isVerifiedUser && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center p-3"
          role="alert"
        >
          <div>
            Your account is not verified. Please verify your account to start
            selling your artworks.
          </div>
          <Button
            title="Verify Account"
            onClick={() => navigate("/dashboard/profile")}
            className="btn-danger"
          />
        </div>
      )}
      {!isTrustapAccountCreated && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center p-3"
          role="alert"
        >
          <div>
            You have not created your Trustap account. Please create your
            Trustap account to start selling your artworks. This account is
            required to receive payouts from your sales.
          </div>
          <Button
            title="Create Trustap Account"
            onClick={() => navigate("/dashboard/trustap-account")}
            className="btn-danger"
          />
        </div>
      )}
      <div className="row g-3">
        <div className="col-6 col-xl-3">
          {counts ? (
            <StatCard
              title="Total active listings"
              value={Utils.formatToNumber(counts?.active || 0)}
              icon="bi-grid-fill"
              tone="primary"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
        <div className="col-6 col-xl-3">
          {counts ? (
            <StatCard
              title="Total listings"
              value={Utils.formatToNumber(counts?.total || 0)}
              icon="bi-collection-fill"
              tone="info"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
        <div className="col-6 col-xl-3">
          {counts ? (
            <StatCard
              title={`${moment().format("MMM")}, bids received`}
              value={Utils.formatToNumber(counts?.bids || 0)}
              icon="bi-hammer"
              tone="warning"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
        <div className="col-6 col-xl-3">
          {counts ? (
            <StatCard
              title={`${moment().format("MMM")}, sales ($)`}
              value={Utils.formatToCurrency2(counts?.sales || 0, 1, "")}
              icon="bi-cash-stack"
              tone="success"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <div className="chartCard p-4">
            <div className="d-md-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 className="mb-1">Earning Reports</h5>
                <p className="mb-0" style={{ opacity: "0.5" }}>
                  Yearly earnings overview
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className={`reportTab ${
                    reportType === "total-bids" ? "is-active" : ""
                  }`}
                  onClick={() => setReportType("total-bids")}
                >
                  <span className="reportTab__icon">
                    <i
                      className="bi bi-bar-chart-line-fill"
                      aria-hidden="true"
                    ></i>
                  </span>
                  <span>Total Bids</span>
                </button>
                <button
                  type="button"
                  className={`reportTab ${
                    reportType === "total-sales" ? "is-active" : ""
                  }`}
                  onClick={() => setReportType("total-sales")}
                >
                  <span className="reportTab__icon">
                    <i className="bi bi-cash-coin" aria-hidden="true"></i>
                  </span>
                  <span>Total Sales</span>
                </button>
              </div>
            </div>
            {reportType === "total-bids" && <SellerYearlyBids />}
            {reportType === "total-sales" && <SellerYearlySales />}
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <ArtworkList />
        </div>
      </div>
    </div>
  );
}

export default Seller;
